import { type ReactNode, SVGProps, useEffect, useId, useRef } from 'react';
import Close from '@engineering/icons/close';
import { useLockBodyScroll } from 'react-use';
import {
  BackgroundOverlay,
  CloseButton,
  FlexContainer,
  DropdownCard,
  DropdownContainer,
  DropdownContent,
  DropdownFrame,
  GridContainer,
  IconButton,
  PanelContainer,
  StyledContent,
  StyledDialog,
  StyledDialogPanel,
} from './MobileMenu.styles';

type MenuProps = {
  id?: string;
  /**
   * The menu of the floating header: `floating` is the full-width panel below
   * md, `dropdown` the card below the button from md, without the dimmed
   * overlay and without the scroll lock. In both, Escape closes the menu, and
   * the focus moves into the menu and back to the button.
   */
  appearance?: 'default' | 'floating' | 'dropdown';
  closeMenuLabel: string;
  icon: React.ComponentType<SVGProps<SVGSVGElement>>;
  iconLabel: string;
  renderContent: (closeMenu: () => void, open: boolean) => ReactNode;
  open: boolean;
  toggleOpen: () => void;
  topPosition: number | null;
  onClose?: () => void;
};

const DEFAULT_HEADER_HEIGHT = 32;

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const MobileMenu = ({
  id,
  appearance = 'default',
  closeMenuLabel,
  icon,
  iconLabel,
  onClose,
  open,
  toggleOpen,
  topPosition,
  renderContent,
}: MenuProps) => {
  const isDropdown = appearance === 'dropdown';
  const isFloating = appearance === 'floating' || isDropdown;

  useLockBodyScroll(open && !isDropdown);

  // The floating header moves the focus back to the button by its ID, so the
  // button needs one also when the menu has none.
  const fallbackId = useId();
  const buttonId = id ?? (isFloating ? fallbackId : undefined);

  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(open);

  const handleClose = () => {
    toggleOpen();
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // Floating header: move the focus into the menu when it opens and back to
  // the button when it closes. The dialog is visible at once when it opens
  // (`$isInstant`), so the focus can move in the same commit.
  useEffect(() => {
    if (isFloating) {
      if (open) {
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
      } else if (wasOpen.current && buttonId) {
        document.getElementById(buttonId)?.focus();
      }
    }
    wasOpen.current = open;
  }, [buttonId, isFloating, open]);

  // Floating header: Escape closes the menu.
  useEffect(() => {
    if (!isFloating || !open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFloating, open]);

  const floatingProps = isFloating ? { 'aria-expanded': open } : {};

  return (
    <>
      <IconButton
        id={buttonId}
        aria-label={iconLabel}
        icon={icon}
        selected={open}
        size="medium"
        onClick={toggleOpen}
        {...floatingProps}
      />

      <StyledDialog
        show={open}
        $isInstant={isFloating}
        $isDropdown={isDropdown}
        topPosition={topPosition || DEFAULT_HEADER_HEIGHT}
      >
        <BackgroundOverlay
          show={open}
          $isTransparent={isDropdown}
          onClick={handleClose}
          data-testid={isFloating ? 'menu-overlay' : undefined}
        />

        {isDropdown ? (
          <DropdownContainer>
            <DropdownFrame>
              <DropdownCard ref={panelRef} data-testid="menu-dropdown">
                <DropdownContent data-testid="menu-content">
                  {renderContent(toggleOpen, open)}
                </DropdownContent>
              </DropdownCard>
            </DropdownFrame>
          </DropdownContainer>
        ) : (
          <PanelContainer>
            <GridContainer>
              <FlexContainer>
                <StyledDialogPanel show={open} ref={panelRef}>
                  <StyledContent
                    data-testid="menu-content"
                    $isFloating={isFloating}
                  >
                    {renderContent(toggleOpen, open)}
                    <CloseButton
                      id={`${id}-close-button`}
                      aria-label={closeMenuLabel}
                      icon={Close}
                      onClick={handleClose}
                      $isFloating={isFloating}
                    />
                  </StyledContent>
                </StyledDialogPanel>
              </FlexContainer>
            </GridContainer>
          </PanelContainer>
        )}
      </StyledDialog>
    </>
  );
};

export { MobileMenu };
